/**
 * Session: auth, bootstrap, wallet, equipment, characters.
 *
 * The wallet here is a *cache of the server's number*, never a source of truth.
 * Every mutation returns the authoritative balance and we overwrite with it —
 * see `applyWallet`. The client is allowed to predict, never to decide.
 */
import { create } from 'zustand';

import { ApiError, needsReauth, tokens } from '@/api/client';
import { clearSnapshot, readAnySnapshot, writeSnapshot } from './persist';
import {
  auth, boot,
  type ActiveCharacterDTO, type BootstrapDTO, type EquipmentDTO, type WalletDTO,
} from '@/api/endpoints';

const DEVICE_KEY = 'shwetap.device';

/** Stable per-install id. The server keys guest accounts off this. */
function deviceUid(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const fresh = globalThis.crypto?.randomUUID?.() ?? `dev-${Date.now()}-${Math.random()}`;
    localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    return `ephemeral-${Date.now()}`;
  }
}

export type Phase = 'booting' | 'needs-login' | 'needs-first-login' | 'ready' | 'error';

export type Provider = 'google' | 'apple' | 'telegram';

/**
 * Remembers that this install has signed in before, so a returning player skips
 * the login screen. Cleared by signOut.
 *
 * Kept separate from the token: tokens expire, this intent does not. Without it
 * an expired refresh would drop a signed-in player back onto the login screen
 * instead of quietly restoring their session.
 */
const RETURNING_KEY = 'shwetap.returning';

/**
 * How this install last signed in: 'guest' or a provider name.
 *
 * This distinction is load-bearing. A guest account is keyed off the device id,
 * so it can be silently recovered. A *linked* account cannot — once a player
 * links Google, the server's guest lookup for this device no longer finds them,
 * and calling /auth/guest would hand back a brand-new empty account. Doing that
 * silently would look exactly like losing all your progress.
 */
type LastMethod = 'guest' | Provider;

function markReturning(method: LastMethod): void {
  try {
    localStorage.setItem(RETURNING_KEY, '1');
    localStorage.setItem(`${RETURNING_KEY}.method`, method);
  } catch { /* private mode */ }
}
function hasReturned(): boolean {
  try { return localStorage.getItem(RETURNING_KEY) === '1'; } catch { return false; }
}
function lastMethod(): LastMethod | null {
  try {
    return (localStorage.getItem(`${RETURNING_KEY}.method`) as LastMethod | null) ?? null;
  } catch { return null; }
}

interface SessionState {
  phase: Phase;
  error: string | null;

  user: BootstrapDTO['user'] | null;
  wallet: WalletDTO;
  energy: BootstrapDTO['energy'];
  equipment: Record<string, EquipmentDTO>;
  characters: { explorer: ActiveCharacterDTO | null; companion: ActiveCharacterDTO | null };
  collection: BootstrapDTO['collection'] | null;
  flags: BootstrapDTO['flags'];
  bestDepth: number;

  /**
   * Restore an existing session, or land on the login screen.
   *
   * This deliberately does NOT create an account: signing a player in without
   * asking makes "Continue as Guest" a lie and takes the Google/Apple/Telegram
   * choice away from them.
   */
  init(): Promise<void>;
  /** Explicitly chosen from the login screen. */
  playAsGuest(): Promise<void>;
  signInWith(provider: Provider, credential: string): Promise<void>;
  /** Keep this device's progress and attach a provider to it. */
  linkProvider(provider: Provider, credential: string): Promise<void>;
  /** Disconnect a provider. Throws if it is the only one — the server decides that. */
  unlinkProvider(provider: Provider): Promise<void>;
  completeFirstLogin(v: {
    display_name: string; date_of_birth: string; country_code: string; locale: string;
  }): Promise<void>;
  refresh(): Promise<void>;
  applyWallet(w: WalletDTO): void;
  applyEnergy(e: BootstrapDTO['energy']): void;
  addXp(level: number, xp: number, xpToNext: number): void;
  signOut(): void;
}

const EMPTY_WALLET: WalletDTO = { coins: 0, kyat: '0.00' };

/**
 * What is cached locally between launches. Deliberately the render-relevant
 * slice only — no tokens (those live in `tokens`), and nothing the server treats
 * as authoritative is trusted from here.
 */
type Snapshot = Pick<
  SessionState,
  'user' | 'wallet' | 'energy' | 'equipment' | 'characters' | 'collection' | 'flags' | 'bestDepth'
>;

export const useSession = create<SessionState>((set, get) => ({
  phase: 'booting',
  error: null,
  user: null,
  wallet: EMPTY_WALLET,
  energy: { current: 0, max: 25, next_regen_in: 0 },
  equipment: {},
  characters: { explorer: null, companion: null },
  collection: null,
  /* ad_reward_* default to "cannot reward": until /bootstrap says otherwise the
     honest assumption is that an ad cannot pay, so we never offer one we might
     not be able to honour. */
  flags: {
    ads_enabled: true, ad_reward_platforms: [], ad_reward_web: false,
    withdraw_enabled: false, min_withdraw: '50.00',
  },
  bestDepth: 0,

  async init() {
    set({ phase: 'booting', error: null });

    /* A first-time install chooses how to sign in. */
    if (!tokens.access && !hasReturned()) {
      set({ phase: 'needs-login' });
      return;
    }

    /* Paint the player's real data from disk before the network answers. This is
       presentation only — `refresh()` below overwrites all of it, and nothing is
       ever spent from these numbers. */
    const cached = readAnySnapshot<Snapshot>();
    if (cached && tokens.access) set({ ...cached.data });

    try {
      if (!tokens.access) {
        /* A guest is recoverable from the device id alone. A linked account is
           not — ask for its provider rather than minting an empty account. */
        if (lastMethod() !== 'guest') {
          set({ phase: 'needs-login' });
          return;
        }
        const res = await auth.guest(deviceUid());
        tokens.save({ access: res.access, refresh: res.refresh });
      }
      await get().refresh();
      markReturning(lastMethod() ?? 'guest');
    } catch (e) {
      if (e instanceof ApiError && needsReauth(e.code, e.status)) {
        /* Stored tokens were stale AND refresh failed. Recover into the same
           device's guest account rather than stranding them on a boot screen. */
        tokens.clear();
        if (lastMethod() !== 'guest') {
          /* Same reasoning as above: never silently replace a linked account. */
          set({ phase: 'needs-login', error: 'Please sign in again.' });
          return;
        }
        try {
          const res = await auth.guest(deviceUid());
          tokens.save({ access: res.access, refresh: res.refresh });
          await get().refresh();
          markReturning('guest');
          return;
        } catch { /* fall through */ }
      }
      if (e instanceof ApiError && (e.code === 'BANNED' || e.code === 'MAINTENANCE')) {
        set({ phase: 'error', error: e.message });
        return;
      }
      set({
        phase: 'error',
        error: e instanceof ApiError ? e.message : 'Could not reach the cave.',
      });
    }
  },

  async playAsGuest() {
    set({ phase: 'booting', error: null });
    try {
      const res = await auth.guest(deviceUid());
      tokens.save({ access: res.access, refresh: res.refresh });
      await get().refresh();
      markReturning('guest');
    } catch (e) {
      set({ phase: 'needs-login', error: e instanceof ApiError ? e.message : 'Sign-in failed.' });
    }
  },

  async signInWith(provider, credential) {
    set({ phase: 'booting', error: null });
    try {
      const res = await auth.social(provider, credential, deviceUid());
      tokens.save({ access: res.access, refresh: res.refresh });
      await get().refresh();
      markReturning(provider);
    } catch (e) {
      set({ phase: 'needs-login', error: e instanceof ApiError ? e.message : 'Sign-in failed.' });
    }
  },

  async linkProvider(provider, credential) {
    /* Throws on purpose: this is called from Settings by a signed-in player, so
       the caller shows the outcome inline instead of switching screens. */
    await auth.linkGuest(provider, credential, deviceUid());
    /* The account is no longer recoverable from the device id alone. */
    markReturning(provider);
    await get().refresh();
  },

  async unlinkProvider(provider) {
    /* Throws on purpose, like `linkProvider`: Settings shows the outcome inline. The
       interesting failure is the server refusing to remove the last identity, and that
       message is the one worth putting in front of the player verbatim. */
    await auth.unlink(provider);
    await get().refresh();
  },

  async refresh() {
    const b = await boot.bootstrap();
    set({
      user: b.user,
      wallet: b.wallet,
      energy: b.energy,
      equipment: b.equipment,
      characters: b.active_characters,
      collection: b.collection,
      flags: b.flags,
      bestDepth: b.run.best_depth_m ?? 0,
      /* The SERVER decides whether the name/DOB form is needed — a guest never
         sees it, a linked account without a DOB does. Re-deriving that rule here
         from tutorial_step is how the two drift apart, and it is what made
         "Play as Guest" open a form instead of the cave. */
      phase: b.user.needs_first_login ? 'needs-first-login' : 'ready',
    });

    /* Snapshot the authoritative answer for the next cold start. */
    writeSnapshot<Snapshot>(b.user.uuid, {
      user: b.user,
      wallet: b.wallet,
      energy: b.energy,
      equipment: b.equipment,
      characters: b.active_characters,
      collection: b.collection,
      flags: b.flags,
      bestDepth: b.run.best_depth_m ?? 0,
    });
  },

  async completeFirstLogin(v) {
    await auth.firstLogin(v);
    await get().refresh();
    set({ phase: 'ready' });
  },

  /** Authoritative overwrite. Never merge — the server's number wins outright. */
  applyWallet(w) {
    set((s) => ({ wallet: { ...s.wallet, ...w } }));
  },

  applyEnergy(e) {
    set({ energy: e });
  },

  addXp(level, xp, xpToNext) {
    set((s) => (s.user ? { user: { ...s.user, level, xp, xp_to_next: xpToNext } } : {}));
  },

  signOut() {
    tokens.clear();
    /* Must go with the tokens: otherwise the next player on a shared device sees
       the previous player's name and balance before the first request lands. */
    clearSnapshot();
    try {
      localStorage.removeItem(RETURNING_KEY);
      localStorage.removeItem(`${RETURNING_KEY}.method`);
    } catch { /* private mode */ }
    /* Back to the login screen, not to a silent new guest account. */
    set({ phase: 'needs-login', error: null, user: null, wallet: EMPTY_WALLET, collection: null });
  },
}));

/* ── selectors ─────────────────────────────────────────────────────────────
   Components subscribe to the narrowest slice so a wallet tick does not
   re-render the tunnel. */

export const selectAxe = (s: SessionState) => s.equipment.axe;
export const selectLamp = (s: SessionState) => s.equipment.lamp;
export const selectCoins = (s: SessionState) => s.wallet.coins;
export const selectKyat = (s: SessionState) => s.wallet.kyat;

/** Depth ceiling from the equipped lamp. `null` from the server means endless. */
export const selectMaxDepth = (s: SessionState): number => {
  const d = s.equipment.lamp?.stats.max_depth_m;
  return d == null || d < 0 ? Number.POSITIVE_INFINITY : d;
};

export const selectLightRadius = (s: SessionState): number =>
  s.equipment.lamp?.stats.light_px ?? 170;
